import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const updateAutomationOrderVariablesSchema = z.object({
  order: z.array(z.uuid()),
});

export type UpdateAutomationOrderVariables = z.infer<typeof updateAutomationOrderVariablesSchema>;

export async function updateAutomationOrderFn(
  axiosInstance: Promise<AxiosInstance>,
  queryKey: UpdateAutomationOrderVariables,
) {
  const instance = await axiosInstance;
  const data = updateAutomationOrderVariablesSchema.parse(queryKey);
  return instance.put(`/automations/order`, data);
}

export function useUpdateAutomationOrder() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateAutomationOrderVariables>({
    mutationKey: ['updateAutomationOrder'],
    mutationFn: variables => updateAutomationOrderFn(axiosInstance, variables),
    onError: error => {
      client.invalidateQueries({ queryKey: ['automationIndex'] });
      notifications.show({
        message:
          error.response?.data.error_message ?? error.response?.data.errors?.[0] ?? error.message,
        color: 'red',
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateAutomationOrderVariables,
    'updateAutomationOrder'
  >('updateAutomationOrder', mutation);
}
